<script setup>
import { computed, ref, watch, onMounted } from 'vue';

const mainLogoError = ref(false);

const props = defineProps({
  card1Start: { type: [Number, String], default: 0 },
  card1: { type: [Number, String], default: 363416.28 },
  card1Middle: { type: [Number, String], default: 180000 },
  card2Start: { type: [Number, String], default: 0 },
  card2: { type: [Number, String], default: 91696.62 },
  card2Middle: { type: [Number, String], default: 45000 },
  card3Start: { type: [Number, String], default: 0 },
  card3: { type: [Number, String], default: 11245.19 },
  card3Middle: { type: [Number, String], default: 5600 },
  card4Start: { type: [Number, String], default: 0 },
  card4: { type: [Number, String], default: 542.28 },
  card4Middle: { type: [Number, String], default: 270 },
  secondsPerUnit: { type: Number, default: 0.5 },
  currencyCode: { type: String, default: 'TRY' },
});

const displayValues = ref([0, 0, 0, 0]);
const pulsingCards = ref([false, false, false, false]);

const formatValue = (val) => {
  const n = Number(val);
  if (isNaN(n)) return '0.00';
  const [int, dec] = n.toFixed(2).split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
};

const linear = (t) => t;

const STORAGE_KEY_START = 'jackpotAnimationStart';
const STORAGE_KEY_CONFIG = 'jackpotCardConfig';

const getCardDuration = (start, end) => {
  const range = Math.abs(Number(end) - Number(start));
  const secPerUnit = props.secondsPerUnit ?? 0.5;
  return Math.max(500, (range / 0.01) * secPerUnit * 1000);
};

const cardProps = computed(() => [
  { start: props.card1Start, end: props.card1, middle: props.card1Middle },
  { start: props.card2Start, end: props.card2, middle: props.card2Middle },
  { start: props.card3Start, end: props.card3, middle: props.card3Middle },
  { start: props.card4Start, end: props.card4, middle: props.card4Middle },
]);

const saveAnimationState = () => {
  try {
    localStorage.setItem(STORAGE_KEY_START, String(Date.now()));
    localStorage.setItem(
      STORAGE_KEY_CONFIG,
      JSON.stringify({
        ends: [props.card1, props.card2, props.card3, props.card4],
        starts: [props.card1Start, props.card2Start, props.card3Start, props.card4Start],
        secondsPerUnit: props.secondsPerUnit,
      })
    );
  } catch (_) {}
};

const loadAndResume = () => {
  try {
    const startStr = localStorage.getItem(STORAGE_KEY_START);
    const configStr = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (!startStr || !configStr) return false;

    const startTime = Number(startStr);
    const config = JSON.parse(configStr);
    const currentEnds = [props.card1, props.card2, props.card3, props.card4].map(Number);
    const savedEnds = (config.ends || []).map(Number);
    const sameConfig =
      config.secondsPerUnit === props.secondsPerUnit &&
      savedEnds.length === 4 &&
      currentEnds.every((v, i) => Math.abs(v - (savedEnds[i] ?? 0)) < 0.01);

    if (!sameConfig) return false;

    const elapsed = Date.now() - startTime;
    const cards = cardProps.value;

    for (let i = 0; i < 4; i++) {
      const cp = cards[i];
      const duration = getCardDuration(cp.start, cp.end);
      const cycleProgress = (elapsed % duration) / duration;
      const eased = linear(cycleProgress);
      const startNum = Number(cp.start) || 0;
      const endNum = Number(cp.end) || 0;
      const middleNum = cp.middle != null && cp.middle !== '' ? Number(cp.middle) : null;
      const current = startNum + (endNum - startNum) * eased;

      displayValues.value[i] = current;
      pulsingCards.value[i] =
        middleNum != null &&
        ((startNum <= endNum && current >= middleNum) || (startNum > endNum && current <= middleNum));

      animateValue(i, cp.start, cp.end, cp.middle, {
        startFrom: current,
        durationMs: duration * (1 - cycleProgress),
        loop: true,
      });
    }
    return true;
  } catch (_) {
    return false;
  }
};

const animateValue = (index, start, end, middle, opts = {}) => {
  const startNum = Number(start) || 0;
  const endNum = Number(end) || 0;
  const middleNum = middle != null && middle !== '' ? Number(middle) : null;
  const duration = opts.durationMs ?? getCardDuration(start, end);
  const animStart = opts.startFrom;
  const useResume = animStart != null;
  const loop = opts.loop === true;

  const startTime = performance.now();
  if (!useResume) pulsingCards.value[index] = false;

  const runCycle = (fromVal, dur) => {
    const cycleStart = performance.now();

    const step = (now) => {
      const elapsed = now - cycleStart;
      const progress = Math.min(elapsed / dur, 1);
      const eased = linear(progress);
      const current = fromVal + (endNum - fromVal) * eased;
      displayValues.value[index] = current;

      if (middleNum != null) {
        const reachedMiddle = (startNum <= endNum && current >= middleNum) || (startNum > endNum && current <= middleNum);
        if (reachedMiddle) {
          pulsingCards.value[index] = true;
        }
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        displayValues.value[index] = endNum;
        pulsingCards.value[index] = false;
        if (loop) {
          displayValues.value[index] = startNum;
          runCycle(startNum, getCardDuration(start, end));
        }
      }
    };

    requestAnimationFrame(step);
  };

  runCycle(useResume ? animStart : startNum, duration);
};

const runAnimations = () => {
  const resumed = loadAndResume();
  if (resumed) return;

  displayValues.value = [
    Number(props.card1Start) || 0,
    Number(props.card2Start) || 0,
    Number(props.card3Start) || 0,
    Number(props.card4Start) || 0,
  ];
  pulsingCards.value = [false, false, false, false];
  saveAnimationState();

  const cards = cardProps.value;
  cards.forEach((cp, i) => animateValue(i, cp.start, cp.end, cp.middle, { loop: true }));
};

onMounted(runAnimations);

watch(
  () => [props.card1, props.card2, props.card3, props.card4, props.card1Start, props.card2Start, props.card3Start, props.card4Start, props.card1Middle, props.card2Middle, props.card3Middle, props.card4Middle, props.secondsPerUnit],
  runAnimations,
  { deep: true }
);

const jackpots = computed(() => [
  { value: displayValues.value[0], pulsing: pulsingCards.value[0] },
  { value: displayValues.value[1], pulsing: pulsingCards.value[1] },
  { value: displayValues.value[2], pulsing: pulsingCards.value[2] },
  { value: displayValues.value[3], pulsing: pulsingCards.value[3] },
]);
</script>

<template>
  <div class="jackpot-cards">
    <div class="jackpot-cards__header">
      <img
        v-show="!mainLogoError"
        src="/jackpot-cards/jackpot-cards-logo.png"
        alt="Jackpot Cards"
        class="jackpot-cards__main-logo"
        @error="mainLogoError = true"
      />
      <div v-show="mainLogoError" class="jackpot-cards__main-logo-fallback">
        <span class="jackpot-cards__title">JACKPOT CARDS</span>
      </div>
    </div>

    <div class="jackpot-cards__bars">
      <div
        v-for="(jp, i) in jackpots"
        :key="i"
        class="jackpot-cards__bar"
        :class="[`jackpot-cards__bar--${i}`, { 'jackpot-cards__bar--pulsing': jp.pulsing }]"
      >
        <div class="jackpot-cards__suit-wrap">
          <img
            :src="`/jackpot-cards/jp${i + 1}.png`"
            :alt="`Card ${i + 1}`"
            class="jackpot-cards__suit-img"
          />
        </div>
        <div class="jackpot-cards__value-wrap">
          <span class="jackpot-cards__value">{{ formatValue(jp.value) }}</span>
        </div>
        <div class="jackpot-cards__currency-section">
          <div class="jackpot-cards__currency-divider" />
          <div class="jackpot-cards__currency-wrap">
            <span
              v-for="letter in (currencyCode || 'TRY').split('')"
              :key="letter"
              class="jackpot-cards__currency-letter"
            >{{ letter }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.jackpot-cards {
  @apply mx-3 mt-4 mb-2;

  border-radius: 10px;
  padding: 1.25rem 1.1rem;
  /* border: 1px solid rgba(232, 197, 71, 0.4);
  box-shadow: 0 0 8px rgba(232, 197, 71, 0.15); */
}

.jackpot-cards__header {
  @apply flex flex-col items-center gap-1 mb-4;
}

.jackpot-cards__main-logo {
  max-height: 52px;
  width: auto;
  object-fit: contain;
}

.jackpot-cards__main-logo-fallback {
  text-align: center;
}

.jackpot-cards__title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #e85c2a;
  text-shadow: 0 0 1px #d4af37, 0 1px 2px rgba(0,0,0,0.5);
  letter-spacing: 0.08em;
  line-height: 1.2;
}

.jackpot-cards__bars {
  @apply flex flex-col items-center gap-2.5;
  overflow: visible;
  padding-left: 12px;
  padding-right: 12px;
}

.jackpot-cards__bar {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(180deg, #0c0e12 0%, #1a1f28 30%, #222830 50%, #1a1f28 70%, #0c0e12 100%);
  border: 1.5px solid #e8c547;
  border-radius: 6px;
  padding: 0.28rem 0.5rem 0.28rem 0.6rem;
  box-shadow: 0 0 12px rgba(232, 197, 71, 0.35), 0 0 4px rgba(212, 175, 55, 0.3), inset 0 1px 0 rgba(255,255,255,0.04);
  width: 100%;
  min-height: 40px;
  overflow: visible;
}

.jackpot-cards__bar--0 { max-width: 100%; }
.jackpot-cards__bar--1 { max-width: 88%; }
.jackpot-cards__bar--2 { max-width: 76%; }
.jackpot-cards__bar--3 { max-width: 64%; }

.jackpot-cards__bar--pulsing {
  animation: jackpot-pulse 0.8s ease-in-out infinite;
}

@keyframes jackpot-pulse {
  0%, 100% {
    box-shadow: 0 0 12px rgba(232, 197, 71, 0.35), 0 0 4px rgba(212, 175, 55, 0.3), inset 0 1px 0 rgba(255,255,255,0.04);
  }
  50% {
    box-shadow: 0 0 20px rgba(232, 197, 71, 0.7), 0 0 12px rgba(212, 175, 55, 0.5), inset 0 1px 0 rgba(255,255,255,0.06);
  }
}

.jackpot-cards__suit-wrap {
  position: absolute;
  left: -26px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  z-index: 2;
  filter: drop-shadow(0 0 2px rgba(0,0,0,0.5));
}

.jackpot-cards__suit-img {
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  object-fit: contain;
}

.jackpot-cards__value-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.15rem 0;
  margin-left: 38px;
}

.jackpot-cards__value {
  font-size: 1.05rem;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.18em;
}

.jackpot-cards__currency-section {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
}

.jackpot-cards__currency-divider {
  width: 1px;
  background: #e8c547;
  margin: 0 0.25rem;
}

.jackpot-cards__currency-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  padding: 0.06rem 0.08rem;
}

.jackpot-cards__currency-letter {
  font-size: 0.55rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
  letter-spacing: 0.02em;
}
</style>